import { Component, ErrorInfo, ReactNode } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { logger } from "@/lib/logger";
import { Button } from "@/components/ui/button";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Boundary de nível de aplicação para o painel admin.
 * Captura erros de render de qualquer rota e exibe um fallback,
 * evitando a tela branca (white screen of death) quando uma
 * rota lança durante a renderização.
 */
export class ErrorBoundary extends Component<Props, State> {
  public state: State = { hasError: false, error: null };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.error("SYSTEM", "Erro não tratado no painel admin", { error, errorInfo });
  }

  private handleReload = () => {
    if (typeof window !== "undefined") {
      window.location.reload();
    }
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center p-8 text-center">
          <AlertTriangle className="mb-4 h-12 w-12 text-yellow-500" />
          <h1 className="mb-2 text-xl font-semibold text-foreground">
            Algo deu errado
          </h1>
          <p className="mb-6 max-w-md text-sm text-muted-foreground">
            Ocorreu um erro inesperado no painel. Tente recarregar a página. Se o
            problema persistir, contate o suporte técnico.
          </p>
          <Button onClick={this.handleReload} className="inline-flex items-center gap-2">
            <RefreshCw className="h-4 w-4" />
            Recarregar
          </Button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
